import React, { useEffect, useMemo } from 'react';
import { assign, chain, toUpper, startCase, split } from 'lodash';
import FileInput from '../FileInput/FileInput';
import Header from '../Header';
import Page from '../Page';
import { Actions, hasPermissionTo, RolesType } from '../../Lib/Constants/roles';

const getFileTypes = (name: string): string[] =>
  name === 'routing_and_product_guidelines'
    ? ['application/zip', 'application/x-zip-compressed']
    : ['application/pdf'];

type PublicFilesProps = {
  publicFilesUrls: object;
  currentUser: { role: string };
  getFiles: (getType: string) => void;
  uploadFile: (formData: any) => any;
};

function PublicFiles({
  publicFilesUrls,
  currentUser,
  getFiles,
  uploadFile,
}: PublicFilesProps): React.ReactElement<PublicFilesProps> {
  const perms = useMemo(() => {
    const canUpload = hasPermissionTo(
      'admin-console/public-files',
      currentUser.role as RolesType,
      Actions.UPDATE,
      'FILE',
    );
    const canDelete = hasPermissionTo(
      'admin-console/public-files',
      currentUser.role as RolesType,
      Actions.DELETE,
      'FILE',
    );
    const canRead = hasPermissionTo(
      'admin-console/public-files',
      currentUser.role as RolesType,
      Actions.READ,
      'FILE',
    );

    return {
      canUpload,
      canDelete,
      canRead,
    };
  }, [currentUser]);

  useEffect(() => {
    getFiles('admin');
  }, []);

  const updateFileField = (name: string, file: File) => {
    const formData = { name, file, type: file.type };
    uploadFile(formData);
  };

  return (
    <Page.Container withoutTwp>
      <Header.Major icon="file/file-settings" title="Manage Files" />
      <Page.Content>
        {chain(publicFilesUrls)
          .map((v, name) => assign({}, v, { name }))
          .sortBy('name')
          .map((file) => {
            const { read, name, exists, loading, progressEvent } = file;
            const splitName = split(name, '_');
            const fileName = splitName.map((s) => startCase(s)).join(' ');

            return (
              <FileInput
                key={name}
                name={name}
                header={toUpper(fileName)}
                suffix={`${splitName.join('-')}`}
                downloadText={`${fileName} Download`}
                disabled={false}
                fullWidth
                fileType={getFileTypes(name)}
                fileLink={read || ''}
                sizeLimit={name === 'routing_and_product_guidelines' ? 500 : 15}
                exists={exists || false}
                loading={loading || false}
                desc={`Please upload the ${fileName} form`}
                progressEvent={progressEvent}
                onChange={updateFileField}
                perms={perms}
              />
            );
          })
          .value()}
      </Page.Content>
    </Page.Container>
  );
}

export default PublicFiles;
